import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import {
  ReactFlow,
  Node,
  Edge,
  Controls,
  MiniMap,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  Panel,
  BackgroundVariant,
  useReactFlow,
  ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Stack,
  Tooltip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import { API_BASE_URL } from '../config/api';
import { api, API_ENDPOINTS } from '../config/api';

// Types matching CHISG API (Weaviate-backed)
interface Skill {
  id: string;           // UUID from Weaviate
  skillName: string;
  domain?: string;
  skillDescription?: string;
  criteria1?: string;
  criteria2?: string;
  criteria3?: string;
  criteria4?: string;
  criteria5?: string;
  developmentAge?: string;
  hidden?: number;
  parentSkills?: { nSkillID: string; skillLinkID?: number }[];
  offspringSkills?: { nSkillID: string; skillLinkID?: number }[];
}

interface SkillLink {
  parentSkillID: string;
  offspringSkillID: string;
}

interface ApiResponse {
  skills: Skill[];
  links: SkillLink[];
}

type FlowNodeData = {
  label?: string;
  skill: Skill;
  domain: string;
};

// Domain color mapping for visual clustering

// Component to trigger fitView when filter changes
const FitViewOnFilterChange: React.FC<{ selectedDomain: string | null; searchTerm: string }> = ({ 
  selectedDomain, 
  searchTerm 
}) => {
  const { fitView } = useReactFlow();
  
  useEffect(() => {
    // Always fit view when filters change, including initial render
    // Small delay to ensure nodes are rendered/positioned
    const timer = setTimeout(() => {
      console.log('[GraphExplorer] Triggering fitView update');
      fitView({ padding: 0.2, duration: 800 });
    }, 100);
    return () => clearTimeout(timer);
  }, [selectedDomain, searchTerm, fitView]);
  
  return null;
};
const DOMAIN_COLORS: Record<string, string> = {
  'COGNITION': '#4CAF50', // Green
  'PROBLEM SOLVING': '#2196F3', // Blue
  'PEOPLE': '#E91E63', // Pink
  'SELF': '#9C27B0', // Purple
  'COMMUNICATION': '#FF9800', // Orange
  'LANGUAGE': '#00BCD4', // Cyan
  'LITERACY': '#795548', // Brown
  'STRATEGIC': '#607D8B', // Blue Grey
  'FOCUS & TOOLS': '#3F51B5', // Indigo
  'NUMBERS': '#F44336', // Red
  'REASONING': '#009688', // Teal
  
  // Aliases from guessDomain
  'MONEY & NUMBERS': '#F44336', 
  'CREATIVITY': '#FFC107', // Amber

  // Mapped/Legacy Aliases
  'MATH': '#F44336', // Maps to NUMBERS
  'SOCIAL-EMOTIONAL': '#E91E63', // Maps to PEOPLE
  'EXECUTIVE': '#3F51B5', // Maps to FOCUS & TOOLS
  'SENSORIMOTOR': '#4CAF50', // Maps to PHYSICAL
  'TECHNOLOGY': '#3F51B5', // Maps to FOCUS & TOOLS

  // Legacy/Fallback mappings
  'FOUNDATIONAL': '#9E9E9E',
  'PHYSICAL': '#4CAF50',
  'default': '#607D8B',
};

// Guess domain from skill name (you can enhance this logic)
function guessDomain(skillName: string): string {
  const name = skillName.toLowerCase();
  if (name.includes('emotion') || name.includes('self') || name.includes('feeling')) return 'SELF';
  if (name.includes('move') || name.includes('physical') || name.includes('body')) return 'PHYSICAL';
  if (name.includes('share') || name.includes('friend') || name.includes('people') || name.includes('social')) return 'PEOPLE';
  if (name.includes('problem') || name.includes('solve') || name.includes('think')) return 'PROBLEM SOLVING';
  if (name.includes('count') || name.includes('number') || name.includes('money') || name.includes('math')) return 'MONEY & NUMBERS';
  if (name.includes('read') || name.includes('write') || name.includes('speak') || name.includes('language') || name.includes('word')) return 'LANGUAGE';
  if (name.includes('create') || name.includes('art') || name.includes('music') || name.includes('imagine')) return 'CREATIVITY';
  if (name.includes('focus') || name.includes('tool') || name.includes('attention')) return 'FOCUS & TOOLS';
  return 'default';
}

// Layout: Hierarchical layout with link-aware positioning
// This algorithm positions linked nodes close together to minimize edge crossings
function layoutNodes(skills: Skill[], links: SkillLink[]): Node<FlowNodeData>[] {
  const skillMap = new Map(skills.map(s => [s.id, s]));
  
  // Build adjacency maps
  const children = new Map<string, string[]>();
  const parents = new Map<string, string[]>();
  
  links.forEach(link => {
    if (!children.has(link.parentSkillID)) children.set(link.parentSkillID, []);
    children.get(link.parentSkillID)!.push(link.offspringSkillID);
    
    if (!parents.has(link.offspringSkillID)) parents.set(link.offspringSkillID, []);
    parents.get(link.offspringSkillID)!.push(link.parentSkillID);
  });
  
  // Calculate depths using a two-level approach:
  // 1. BFS minimum depth from roots → primary group (compact, ~7 groups)
  // 2. Within each BFS group, intra-group longest path → sub-column offset
  //    (handles the case where two nodes in the same BFS group have a direct
  //    parent→child link between them and must be in different sub-columns)
  // Result: visually grouped left-to-right layout, each BFS group separated
  // by a larger gap, with tight sub-columns only where intra-group links exist.

  // All skill IDs for iteration
  const allSkillIds = skills.map(s => s.id);

  console.log('[GraphExplorer] Starting BFS+intra-group layout with', allSkillIds.length, 'skills and', links.length, 'links');

  // Compute indegrees (used to identify root nodes)
  const indegree = new Map<string, number>();
  allSkillIds.forEach(id => indegree.set(id, 0));
  links.forEach(link => {
    indegree.set(link.offspringSkillID, (indegree.get(link.offspringSkillID) || 0) + 1);
  });

  // ── Step 1: BFS minimum depth from roots ────────────────────────────────
  const bfsDepth = new Map<string, number>();
  const bfsQueue: string[] = [];
  allSkillIds.forEach(id => {
    if ((indegree.get(id) || 0) === 0) {
      bfsDepth.set(id, 0);
      bfsQueue.push(id);
    }
  });
  let bfsHead = 0;
  while (bfsHead < bfsQueue.length) {
    const bfsNodeId = bfsQueue[bfsHead++];
    const bfsD = bfsDepth.get(bfsNodeId)!;
    for (const childId of (children.get(bfsNodeId) || [])) {
      if (!bfsDepth.has(childId)) {
        bfsDepth.set(childId, bfsD + 1);
        bfsQueue.push(childId);
      }
    }
  }
  allSkillIds.forEach(id => { if (!bfsDepth.has(id)) bfsDepth.set(id, 0); });

  // ── Step 2: Within each BFS group, intra-group longest path ─────────────
  // Nodes in the same BFS group can still have parent→child edges between them
  // (e.g. A→B→C and A→C both exist: B and C end up in BFS group 1, but B→C
  // means they need different sub-columns within that group).
  const subDepth = new Map<string, number>();
  const byBfsGroup = new Map<number, string[]>();
  bfsDepth.forEach((d, id) => {
    if (!byBfsGroup.has(d)) byBfsGroup.set(d, []);
    byBfsGroup.get(d)!.push(id);
  });

  byBfsGroup.forEach((groupNodes) => {
    const groupSet = new Set(groupNodes);
    const gIndegree = new Map<string, number>();
    groupNodes.forEach(id => gIndegree.set(id, 0));
    groupNodes.forEach(id => {
      for (const childId of (children.get(id) || [])) {
        if (groupSet.has(childId)) {
          gIndegree.set(childId, (gIndegree.get(childId) || 0) + 1);
        }
      }
    });
    const gQueue: string[] = [];
    const gDepths = new Map<string, number>();
    groupNodes.forEach(id => {
      if ((gIndegree.get(id) || 0) === 0) {
        gDepths.set(id, 0);
        gQueue.push(id);
      }
    });
    let gHead = 0;
    while (gHead < gQueue.length) {
      const id = gQueue[gHead++];
      const d = gDepths.get(id)!;
      for (const childId of (children.get(id) || [])) {
        if (!groupSet.has(childId)) continue;
        gDepths.set(childId, Math.max(gDepths.get(childId) ?? -1, d + 1));
        gIndegree.set(childId, (gIndegree.get(childId) || 0) - 1);
        if (gIndegree.get(childId) === 0) gQueue.push(childId);
      }
    }
    groupNodes.forEach(id => subDepth.set(id, gDepths.get(id) ?? 0));
  });

  // ── Step 3: Effective column = cumulative BFS group offset + sub-depth ───
  const sortedBfsGroups = Array.from(byBfsGroup.keys()).sort((a, b) => a - b);
  const groupColOffset = new Map<number, number>();
  let colOffset = 0;
  sortedBfsGroups.forEach(bfsD => {
    groupColOffset.set(bfsD, colOffset);
    const maxSub = Math.max(...byBfsGroup.get(bfsD)!.map(id => subDepth.get(id)!), 0);
    colOffset += maxSub + 1;
  });

  const depths = new Map<string, number>();
  allSkillIds.forEach(id => {
    const bfsD = bfsDepth.get(id) ?? 0;
    const subD = subDepth.get(id) ?? 0;
    depths.set(id, (groupColOffset.get(bfsD) ?? 0) + subD);
  });

  // DEBUG: Log the calculated depths
  const depthArray = Array.from(depths.entries())
    .sort((a, b) => a[1] - b[1])
    .slice(0, 20);
  console.log('[GraphExplorer] Sample depths (first 20 sorted):', depthArray.map(([id, d]) => `${id}: ${d}`).join(', '));
  console.log('[GraphExplorer] Depth range (BFS+intra-group, effective cols):', Math.min(...Array.from(depths.values())), '-', Math.max(...Array.from(depths.values())), '| BFS groups:', sortedBfsGroups.length);
  console.log('[GraphExplorer] Total depths assigned:', depths.size, 'out of', allSkillIds.length);

  // Assign depths to orphans (disconnected nodes with no parents and no children)
  // Instead of Level 0, we'll put them in a special "Unconnected" bucket to position them separately
  const orphans: string[] = [];
  
  skills.forEach(s => {
    // If no parents and no children, it's an orphan
    const p = parents.get(s.id) || [];
    const c = children.get(s.id) || [];
    if (p.length === 0 && c.length === 0) {
        orphans.push(s.id);
    } else if (!depths.has(s.id)) {
      // Should have been caught by getDepth, but just in case
      depths.set(s.id, 0);
    }
  });

  // Group by depth
  const byDepth = new Map<number, string[]>();
  depths.forEach((depth, id) => {
    if (orphans.includes(id)) return; // Skip orphans in normal hierarchy
    if (!byDepth.has(depth)) byDepth.set(depth, []);
    byDepth.get(depth)!.push(id);
  });
  
  // Compact depths (remove empty levels)
  const sortedDepths = Array.from(byDepth.keys()).sort((a, b) => a - b);
  const compactedLevels: string[][] = sortedDepths.map(d => byDepth.get(d)!);
  
  console.log('[GraphExplorer] Depth grouping:', sortedDepths.map(d => `Depth ${d}: ${byDepth.get(d)!.length} nodes`).join(', '));
  console.log('[GraphExplorer] Compacted levels count:', compactedLevels.length);
  
  // Calculate average position of connected nodes in adjacent level
  const getAverageLinkedPosition = (
    nodeId: string, 
    adjacentLevel: string[], 
    positions: Map<string, number>,
    isParentLevel: boolean
  ): number => {
    const linkedIds = isParentLevel 
      ? (parents.get(nodeId) || [])
      : (children.get(nodeId) || []);
    
    const linkedPositions = linkedIds
      .filter(id => adjacentLevel.includes(id) && positions.has(id))
      .map(id => positions.get(id)!);
    
    if (linkedPositions.length === 0) return -1; // No linked nodes
    return linkedPositions.reduce((sum, pos) => sum + pos, 0) / linkedPositions.length;
  };
  
  // Order nodes within each level to minimize edge crossings
  // Uses barycenter heuristic: position node at average of its connected neighbors
  const orderedLevels: string[][] = [];
  const nodePositions = new Map<string, number>(); // nodeId -> position within level
  
  compactedLevels.forEach((level, levelIndex) => {
    if (levelIndex === 0) {
      // Root level: order by number of children (most connected first for better spread)
      const sorted = [...level].sort((a, b) => {
        const aChildren = children.get(a)?.length || 0;
        const bChildren = children.get(b)?.length || 0;
        return bChildren - aChildren; // More children first
      });
      orderedLevels.push(sorted);
      sorted.forEach((id, idx) => nodePositions.set(id, idx));
    } else {
      // Subsequent levels: order by average position of parents
      const prevLevel = orderedLevels[levelIndex - 1];
      
      // Calculate barycenter for each node
      const barycenters = level.map(id => ({
        id,
        barycenter: getAverageLinkedPosition(id, prevLevel, nodePositions, true)
      }));
      
      // Sort by barycenter, keeping nodes with no parents at the end
      barycenters.sort((a, b) => {
        if (a.barycenter < 0 && b.barycenter < 0) return 0;
        if (a.barycenter < 0) return 1;
        if (b.barycenter < 0) return -1;
        return a.barycenter - b.barycenter;
      });
      
      const sorted = barycenters.map(b => b.id);
      orderedLevels.push(sorted);
      sorted.forEach((id, idx) => nodePositions.set(id, idx));
    }
  });
  
  // Multi-pass refinement: sweep down and up to further optimize positions
  for (let pass = 0; pass < 3; pass++) {
    // Sweep down (from roots to leaves)
    for (let levelIndex = 1; levelIndex < orderedLevels.length; levelIndex++) {
      const level = orderedLevels[levelIndex];
      const prevLevel = orderedLevels[levelIndex - 1];
      
      const barycenters = level.map(id => ({
        id,
        barycenter: getAverageLinkedPosition(id, prevLevel, nodePositions, true)
      }));
      
      barycenters.sort((a, b) => {
        if (a.barycenter < 0 && b.barycenter < 0) return 0;
        if (a.barycenter < 0) return 1;
        if (b.barycenter < 0) return -1;
        return a.barycenter - b.barycenter;
      });
      
      const sorted = barycenters.map(b => b.id);
      orderedLevels[levelIndex] = sorted;
      sorted.forEach((id, idx) => nodePositions.set(id, idx));
    }
    
    // Sweep up (from leaves to roots)
    for (let levelIndex = orderedLevels.length - 2; levelIndex >= 0; levelIndex--) {
      const level = orderedLevels[levelIndex];
      const nextLevel = orderedLevels[levelIndex + 1];
      
      const barycenters = level.map(id => ({
        id,
        barycenter: getAverageLinkedPosition(id, nextLevel, nodePositions, false)
      }));
      
      barycenters.sort((a, b) => {
        if (a.barycenter < 0 && b.barycenter < 0) return 0;
        if (a.barycenter < 0) return 1;
        if (b.barycenter < 0) return -1;
        return a.barycenter - b.barycenter;
      });
      
      const sorted = barycenters.map(b => b.id);
      orderedLevels[levelIndex] = sorted;
      sorted.forEach((id, idx) => nodePositions.set(id, idx));
    }
  }
  
  // ============================================
  // FORCE-DIRECTED RELAXATION
  // Pull connected nodes closer together by adjusting Y positions
  // while maintaining level (X) positions and avoiding overlaps
  // ============================================
  
  const HORIZONTAL_SPACING = 240;
  const VERTICAL_SPACING = 100;
  const MIN_NODE_GAP = 60; // Minimum vertical gap between nodes
  const GROUP_SEPARATION = 140; // Extra horizontal gap at each BFS group boundary

  // Build lookup: for each effective column, how much cumulative extra offset
  // has been added by BFS group separators (so groups are visually distinct).
  const groupStartCols = new Set<number>(
    sortedBfsGroups.map(bfsD => groupColOffset.get(bfsD)!)
  );
  const maxEffectiveCol = Math.max(...Array.from(depths.values()), 0);
  const extraGroupOffset: number[] = [];
  let extraSoFar = 0;
  for (let col = 0; col <= maxEffectiveCol; col++) {
    if (groupStartCols.has(col) && col > 0) extraSoFar += GROUP_SEPARATION;
    extraGroupOffset[col] = extraSoFar;
  }

  // Initialize node coordinates
  const nodeCoords = new Map<string, { x: number; y: number; level: number }>();
  const maxWidth = Math.max(...orderedLevels.map(l => l.length), 1);

  orderedLevels.forEach((level, levelIndex) => {
    const levelWidth = level.length;
    const centerOffset = (maxWidth - levelWidth) / 2;
    const effectiveCol = sortedDepths[levelIndex];
    const groupOffset = extraGroupOffset[effectiveCol] ?? 0;

    level.forEach((id, positionIndex) => {
      nodeCoords.set(id, {
        x: levelIndex * HORIZONTAL_SPACING + groupOffset,
        y: (centerOffset + positionIndex) * VERTICAL_SPACING,
        level: levelIndex
      });
    });
  });
  
  // Force simulation parameters
  const SPRING_STRENGTH = 0.05;  // Reduced from 0.15 to prevent shooting off
  const REPULSION_STRENGTH = 0.3; // How strongly overlapping nodes repel
  const DAMPING = 0.5;  // Increased damping (lower number) to reduce oscillation
  const ITERATIONS = 50;
  
  // Build all edges for force calculation
  const allEdges: { source: string; target: string }[] = links.map((l: SkillLink) => ({
    source: l.parentSkillID,
    target: l.offspringSkillID
  }));
  
  // Velocity for each node (only Y direction matters)
  const velocities = new Map<string, number>();
  skills.forEach(s => velocities.set(s.id, 0));
  
  // Run force simulation
  for (let iter = 0; iter < ITERATIONS; iter++) {
    const forces = new Map<string, number>();
    skills.forEach(s => forces.set(s.id, 0));
    
    // Spring forces: pull connected nodes toward each other's Y position
    allEdges.forEach(edge => {
      const sourceCoord = nodeCoords.get(edge.source);
      const targetCoord = nodeCoords.get(edge.target);
      if (!sourceCoord || !targetCoord) return;
      
      const dy = targetCoord.y - sourceCoord.y;
      const force = dy * SPRING_STRENGTH;
      
      // Apply force to both nodes (pull toward each other)
      // Clamp forces to prevent explosion
      const MAX_FORCE = 1000;
      const clampedForce = Math.max(-MAX_FORCE, Math.min(MAX_FORCE, force));
      
      forces.set(edge.source, (forces.get(edge.source) || 0) + clampedForce);
      forces.set(edge.target, (forces.get(edge.target) || 0) - clampedForce);
    });
    
    // Repulsion forces: push overlapping nodes apart (within same level)
    orderedLevels.forEach(level => {
      for (let i = 0; i < level.length; i++) {
        for (let j = i + 1; j < level.length; j++) {
          const nodeA = level[i];
          const nodeB = level[j];
          const coordA = nodeCoords.get(nodeA);
          const coordB = nodeCoords.get(nodeB);
          if (!coordA || !coordB) continue;
          
          let dy = coordB.y - coordA.y;
          // Handle zero or very small dy to avoid instability
          if (Math.abs(dy) < 0.1) dy = 0.1;
          
          const distance = Math.abs(dy);
          
          // If nodes are too close, push them apart
          if (distance < MIN_NODE_GAP) {
            const overlap = MIN_NODE_GAP - distance;
            const repulsion = overlap * REPULSION_STRENGTH * (dy >= 0 ? 1 : -1);
            
            forces.set(nodeA, (forces.get(nodeA) || 0) - repulsion);
            forces.set(nodeB, (forces.get(nodeB) || 0) + repulsion);
          }
        }
      }
    });
    
    // Update velocities and positions
    skills.forEach(skill => {
      const coord = nodeCoords.get(skill.id);
      if (!coord) return;
      
      let velocity = velocities.get(skill.id) || 0;
      velocity = (velocity + (forces.get(skill.id) || 0)) * DAMPING;
      
      // Clamp velocity to prevent explosion
      const MAX_VELOCITY = 500;
      velocity = Math.max(-MAX_VELOCITY, Math.min(MAX_VELOCITY, velocity));
      
      velocities.set(skill.id, velocity);
      
      coord.y += velocity;
      
      // Safety Clamp on Position
      if (Math.abs(coord.y) > 100000) {
        coord.y = Math.max(-100000, Math.min(100000, coord.y));
        velocities.set(skill.id, 0); // Kill velocity if out of bounds
      }
    });
  }
  
  // Final pass: ensure minimum gaps within each level
  orderedLevels.forEach(level => {
    // Sort by Y position
    const sorted = [...level].sort((a, b) => {
      const coordA = nodeCoords.get(a);
      const coordB = nodeCoords.get(b);
      return (coordA?.y || 0) - (coordB?.y || 0);
    });
    
    // Enforce minimum gaps
    for (let i = 1; i < sorted.length; i++) {
      const prevCoord = nodeCoords.get(sorted[i - 1]);
      const currCoord = nodeCoords.get(sorted[i]);
      if (!prevCoord || !currCoord) continue;
      
      if (currCoord.y - prevCoord.y < MIN_NODE_GAP) {
        currCoord.y = prevCoord.y + MIN_NODE_GAP;
      }
    }
  });
  
  // ============================================
  // CREATE FINAL NODES
  // ============================================
  
  const nodes: Node<FlowNodeData>[] = [];
  
  skills.forEach(skill => {
    let x = 0;
    let y = 0;
    
    if (orphans.includes(skill.id)) {
        // Position orphans in a grid below the main graph
        const ORPHAN_START_Y = (orderedLevels.reduce((max, level) => Math.max(max, level.length), 0) * VERTICAL_SPACING) + 500;
        const ORPHANS_PER_ROW = 10;
        const orphanIndex = orphans.indexOf(skill.id);
        const col = orphanIndex % ORPHANS_PER_ROW;
        const row = Math.floor(orphanIndex / ORPHANS_PER_ROW);
        
        x = col * 300; // Wider spacing
        y = ORPHAN_START_Y + (row * 150);
    } else {    
        const coord = nodeCoords.get(skill.id);
        if (!coord) return;
        x = coord.x;
        y = coord.y;
    }
    
    // Use actual domain from API, fallback to guessing if not available
    const domain = skill.domain || guessDomain(skill.skillName);
    const color = DOMAIN_COLORS[domain] || DOMAIN_COLORS.default;
    
    nodes.push({
      id: skill.id,
      position: { x, y },
      data: { 
        label: skill.skillName,
        skill,
        domain,
      },
      style: {
        background: color,
        color: domain === 'CREATIVITY' ? '#333' : '#fff',
        border: '2px solid #333',
        borderRadius: '8px',
        padding: '10px 16px',
        fontSize: '12px',
        fontWeight: 500,
        minWidth: '120px',
        textAlign: 'center' as const,
        cursor: 'pointer',
      },
    });
  });
  
  return nodes;
}

function createEdges(links: SkillLink[]): Edge[] {
  console.log('[createEdges] Processing', links.length, 'links');
  if (links.length > 0) {
    console.log('[createEdges] Sample link:', links[0], 'keys:', Object.keys(links[0]));
  }
  
  return links
    .filter(link => {
      if (!link.parentSkillID || !link.offspringSkillID) {
        console.warn('[createEdges] Skipping link with missing IDs:', link);
        return false;
      }
      return true;
    })
    .map(link => {
      const highlight = typeof window !== 'undefined' && window.location.hash.includes('highlightEdges');
      return {
        id: `e${link.parentSkillID}-${link.offspringSkillID}`,
        source: String(link.parentSkillID),
        target: String(link.offspringSkillID),
        type: 'smoothstep',
        animated: !!highlight,
        style: { stroke: highlight ? '#FF5722' : '#666', strokeWidth: highlight ? 3 : 2 },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: highlight ? '#FF5722' : '#666',
        },
      };
    });
}

// Quick cycle detection (best-effort). Returns number of cycles found (up to maxCycles).
function detectCycles(links: SkillLink[], maxCycles: number = 20): number {
  const adj = new Map<string, string[]>();
  links.forEach(l => {
    if (!adj.has(l.parentSkillID)) adj.set(l.parentSkillID, []);
    adj.get(l.parentSkillID)!.push(l.offspringSkillID);
  });

  const visited = new Set<string>();
  const instack = new Set<string>();
  const cycles: string[][] = [];

  const dfs = (node: string, path: string[]) => {
    if (cycles.length >= maxCycles) return;
    if (instack.has(node)) {
      // found cycle, record the slice
      const idx = path.indexOf(node);
      if (idx >= 0) cycles.push(path.slice(idx).concat(node));
      else cycles.push([node]);
      return;
    }
    if (visited.has(node)) return;
    visited.add(node);
    instack.add(node);
    const neigh = adj.get(node) || [];
    for (const n of neigh) {
      if (cycles.length >= maxCycles) break;
      dfs(n, path.concat([n]));
    }
    instack.delete(node);
  };

  const nodes = new Set<string>();
  links.forEach(l => { nodes.add(l.parentSkillID); nodes.add(l.offspringSkillID); });
  for (const n of Array.from(nodes)) {
    if (cycles.length >= maxCycles) break;
    if (!visited.has(n)) dfs(n, [n]);
  }

  return cycles.length;
}

// Edge styling constants
const DEFAULT_EDGE_STYLE = { stroke: '#666', strokeWidth: 2 };
const HIGHLIGHT_EDGE_STYLE = { stroke: '#FF5722', strokeWidth: 4 };
const HIGHLIGHT_COLOR = '#FF5722';

interface GraphExplorerProps {
  filteredSkillIds?: string[];
  selectedCourseId?: string | null;
  selectedDomainProp?: string | null;
  selectedSubjectProp?: string | null;
  showSearch?: boolean;
  showDomains?: boolean;
} 

export const GraphExplorer: React.FC<GraphExplorerProps> = ({ 
  filteredSkillIds = [],
  selectedCourseId = null,
  selectedDomainProp = null,
  selectedSubjectProp = null,
  showSearch = true,
  showDomains = true,
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState<Node<FlowNodeData>>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [allSkills, setAllSkills] = useState<Skill[]>([]);
  const [allLinks, setAllLinks] = useState<SkillLink[]>([]);
  const [selectedNodes, setSelectedNodes] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState<Skill | null>(null);
  const [clickedNodeId, setClickedNodeId] = useState<string | null>(null);
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  
  // Sync selectedDomainProp with internal state
  useEffect(() => {
    if (selectedDomainProp !== undefined) {
      console.log('[GraphExplorer] Syncing domain prop:', selectedDomainProp);
      setSelectedDomain(selectedDomainProp);
    }
  }, [selectedDomainProp]);

  // Sync selectedSubjectProp but maybe convert to ids?
  useEffect(() => {
    if (selectedSubjectProp !== undefined) {
       console.log('[GraphExplorer] Syncing subject prop:', selectedSubjectProp);
       setSelectedSubject(selectedSubjectProp);
    }
  }, [selectedSubjectProp]);
  
  // Effect to handle external skill filtering (by ID or by name from course selection)
  useEffect(() => {
    if (filteredSkillIds.length > 0 && nodes.length > 0) {
      const filterSet = new Set(filteredSkillIds);
      // Build a name-based lookup from allSkills so course core_skills (names) work
      const nameToId = new Map<string, string>();
      allSkills.forEach(s => {
        if (s.skillName) nameToId.set(s.skillName.toLowerCase().trim(), s.id);
      });

      // Debug: show filter values and name mapping stats
      console.log('[GraphExplorer] Applying filterSet:', Array.from(filterSet));
      console.log('[GraphExplorer] nameToId size:', nameToId.size);

      let totalMatches = 0;
      setNodes(nds => nds.map(node => {
        const matchById = filterSet.has(node.id);
        const nodeData = node.data as FlowNodeData;
        const label = nodeData?.label ? nodeData.label.toString() : '';
        const matchByName = label ? (filterSet.has(label) || filterSet.has(label.toLowerCase()) || filterSet.has(label.trim())) : false;
        // Also check if any filter value is a name that maps to this node's id
        let matchByNameLookup = false;
        filterSet.forEach(val => {
          const key = val.toLowerCase().trim();
          const resolvedId = nameToId.get(key);
          if (resolvedId === node.id) matchByNameLookup = true;
        });
        const matched = (matchById || matchByName || matchByNameLookup);
        if (matched) totalMatches++;
        return {
          ...node,
          hidden: !matched,
        };
      }));
      console.log('[GraphExplorer] Total matches after filter:', totalMatches);
    } else if (filteredSkillIds.length === 0 && nodes.length > 0) {
      // Reset visibility when filter cleared
      setNodes(nds => nds.map(node => ({
        ...node,
        hidden: false,
      })));
    }
  }, [filteredSkillIds, nodes.length, setNodes, allSkills]);

  // Fetch skills and links
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Use CHISG endpoint (Weaviate-backed, no auth required)
        console.log('[GraphExplorer] window.location:', window.location.href, 'port', window.location.port);
        console.log('[GraphExplorer] ENV API_URL:', (window as any).ENV?.API_URL || 'no window.ENV');
        console.log('[GraphExplorer] API_BASE_URL:', API_BASE_URL);
        console.log('[GraphExplorer] process.env.REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
        console.log('[GraphExplorer] Fetching CHISG from', API_ENDPOINTS.chisg.skillsAndLinks);
        const response = await fetch(API_ENDPOINTS.chisg.skillsAndLinks);
        if (!response.ok) throw new Error('Failed to fetch skills');
        const data = await response.json();
        
        console.log('[GraphExplorer] Raw API response:', data);
        console.log('[GraphExplorer] Response keys:', Object.keys(data));
        console.log('[GraphExplorer] skills field type:', typeof data.skills, 'length:', Array.isArray(data.skills) ? data.skills.length : 'not array');
        console.log('[GraphExplorer] links field type:', typeof data.links, 'length:', Array.isArray(data.links) ? data.links.length : 'not array');
        
        // Try to handle different response formats
        let skills: Skill[] = data.skills || data.Skills || [];
        let links: SkillLink[] = data.links || data.Links || [];
        
        console.log('[GraphExplorer] After normalization - skills:', skills.length, 'links:', links.length);
        console.log('[GraphExplorer] Raw skills sample:', skills?.[0]);
        console.log('[GraphExplorer] Raw links sample:', links?.[0]);
        
        // DEBUG: Log all unique domains from API
        const uniqueDomains = Array.from(new Set(skills.map(s => s.domain)));
        console.log('[COLOR DEBUG] Unique domains from API:', uniqueDomains);
        console.log('[COLOR DEBUG] DOMAIN_COLORS keys:', Object.keys(DOMAIN_COLORS));
        
        // Check for mismatches
        const missingColors = uniqueDomains.filter(d => d && !DOMAIN_COLORS[d]);
        if (missingColors.length > 0) {
          console.warn('[COLOR DEBUG] ⚠️ Domains with NO color mapping:', missingColors);
        }
        
        setAllSkills(skills);
        setAllLinks(links);
        
        // Detect cycles and warn (cycles can break hierarchical layouts)
        try {
          const cycleCount = detectCycles(links || []);
          if (cycleCount > 0) {
            console.warn(`[GraphExplorer] Detected ${cycleCount} cycle(s) in CHISG links — this may affect the hierarchical layout and cause overlapping or layout issues.`);
          }
        } catch (e) {
          console.warn('[GraphExplorer] Cycle detection failed:', e);
        }

        const layoutedNodes = layoutNodes(skills || [], links || []);
        const layoutedEdges = createEdges(links || []);
        
        console.log('[GraphExplorer] VERSION CHECK: v6.0 - Contextual Filters');
        console.log('[GraphExplorer] Skills loaded:', skills?.length);
        console.log('[GraphExplorer] Links loaded:', links?.length);
        
        // Count orphans (no parents, no offspring)
        const parentIds = new Set(links.map((l: SkillLink) => l.parentSkillID));
        const childIds = new Set(links.map((l: SkillLink) => l.offspringSkillID));
        const orphans = skills.filter(s => !parentIds.has(s.id) && !childIds.has(s.id));
        console.log('[GraphExplorer] Orphan count:', orphans.length);
        
        console.log('[GraphExplorer] Nodes created:', layoutedNodes.length);
        if (layoutedNodes.length > 0) {
            console.log('[GraphExplorer] First node:', layoutedNodes[0]);
            console.log('[GraphExplorer] First node hidden:', layoutedNodes[0].hidden);
            console.log('[GraphExplorer] First node position:', layoutedNodes[0].position);
        }
        
        setNodes(layoutedNodes);
        setEdges(layoutedEdges);
        console.log('[GraphExplorer] Edges created:', layoutedEdges.length, layoutedEdges.slice(0,5));
        setError(null);
      } catch (err: any) {
        console.error('Failed to fetch skills:', err);
        setError(err.message || 'Failed to load skills graph');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Handle node click - highlight connected edges
  const onNodeClick = useCallback((event: React.MouseEvent, node: Node<FlowNodeData>) => {
    const skill = node.data.skill;
    const nodeId = node.id;
    
    // Toggle: if clicking the same node, deselect; otherwise select new node
    if (clickedNodeId === nodeId) {
      setClickedNodeId(null);
      // Reset all edges to default style
      setEdges(eds => eds.map(edge => ({
        ...edge,
        style: DEFAULT_EDGE_STYLE,
        animated: false,
        markerEnd: { type: MarkerType.ArrowClosed, color: '#666' },
        zIndex: 0,
      })));
    } else {
      setClickedNodeId(nodeId);
      // Highlight edges connected to this node
      setEdges(eds => eds.map(edge => {
        const isConnected = edge.source === nodeId || edge.target === nodeId;
        return {
          ...edge,
          style: isConnected ? HIGHLIGHT_EDGE_STYLE : { ...DEFAULT_EDGE_STYLE, opacity: 0.3 },
          animated: isConnected,
          markerEnd: { 
            type: MarkerType.ArrowClosed, 
            color: isConnected ? HIGHLIGHT_COLOR : '#666' 
          },
          zIndex: isConnected ? 1000 : 0,
        };
      }));
    }
    
    setSelectedSkill(skill);
    setDrawerOpen(true);
  }, [clickedNodeId, setEdges]);

  // Handle selection change
  const onSelectionChange = useCallback(({ nodes: selectedFlowNodes }: { nodes: Node<FlowNodeData>[] }) => {
    setSelectedNodes(new Set(selectedFlowNodes.map(n => n.id)));
  }, []);

  // Filter nodes by search and domain, reposition compactly when filtered
  const filteredNodes = useMemo(() => {
    // If an explicit skill filter is active (from course selection / AI / search results),
    // respect that first. We accept arrays of skill IDs or skill names.
    if (filteredSkillIds && filteredSkillIds.length > 0) {
      const resolvedIds = new Set<string>();

      // Build name -> id lookup
      const nameToId = new Map<string, string>();
      allSkills.forEach(s => {
        if (s.skillName) nameToId.set(s.skillName.toLowerCase().trim(), s.id);
      });

      // Resolve each filter token to a skill id if possible
      filteredSkillIds.forEach(token => {
        if (!token) return;
        const t = token.toLowerCase().trim();
        // If token already looks like a UUID/id and matches a node, accept directly
        const nodeById = nodes.find(n => n.id === token);
        if (nodeById) {
          resolvedIds.add(token);
          return;
        }
        // Check direct name match
        if (nameToId.has(t)) {
          resolvedIds.add(nameToId.get(t)!);
          return;
        }
        // Check label match on nodes
        const nodeByLabel = nodes.find(n => (n.data.label ?? '').toLowerCase().trim() === t);
        if (nodeByLabel) {
          resolvedIds.add(nodeByLabel.id);
          return;
        }
      });

      // Create base filtered set
      const baseFiltered = nodes.map(node => ({ ...node, hidden: !resolvedIds.has(node.id) }));

      // If no visible nodes, just return base filtered
      if (!baseFiltered.some(n => !n.hidden)) return baseFiltered;

      // Helper: estimate node height (duplicate of later helper for local use)
      const estimateNodeHeightLocal = (label: string): number => {
        const NODE_MIN_WIDTH = 120; // from node style
        const CHAR_WIDTH = 7; // approximate pixels per character
        const LINE_HEIGHT = 20; // approximate line height
        const PADDING = 24; // vertical padding

        const charsPerLine = Math.floor(NODE_MIN_WIDTH / CHAR_WIDTH);
        const numLines = Math.ceil(label.length / charsPerLine);
        return Math.max(50, numLines * LINE_HEIGHT + PADDING);
      };

      // Recalculate compact layout for visible nodes (same logic as domain/search compaction)
      const visibleNodes = baseFiltered.filter(n => !n.hidden);
      const visibleIds = new Set(visibleNodes.map(n => n.id));

      const visibleParents = new Map<string, string[]>();
      allLinks.forEach(link => {
        const parentId = link.parentSkillID;
        const childId = link.offspringSkillID;
        if (visibleIds.has(parentId) && visibleIds.has(childId)) {
          if (!visibleParents.has(childId)) visibleParents.set(childId, []);
          visibleParents.get(childId)!.push(parentId);
        }
      });

      const depths = new Map<string, number>();
      const getDepth = (nodeId: string, visited: Set<string> = new Set()): number => {
        if (depths.has(nodeId)) return depths.get(nodeId)!;
        if (visited.has(nodeId)) return 0; // Cycle detection
        visited.add(nodeId);

        const parentIds = visibleParents.get(nodeId) || [];
        if (parentIds.length === 0) {
          depths.set(nodeId, 0);
          return 0;
        }

        const maxParentDepth = Math.max(...parentIds.map(pid => getDepth(pid, new Set(visited))));
        const depth = maxParentDepth + 1;
        depths.set(nodeId, depth);
        return depth;
      };

      visibleNodes.forEach(n => getDepth(n.id));

      // Group by recalculated depth
      const byLevel = new Map<number, typeof visibleNodes>();
      visibleNodes.forEach(node => {
        const level = depths.get(node.id) || 0;
        if (!byLevel.has(level)) byLevel.set(level, []);
        byLevel.get(level)!.push(node);
      });

      // Compact positioning
      const levels = Array.from(byLevel.keys()).sort((a, b) => a - b);
      const COMPACT_H_SPACING = 300;
      const V_GAP = 15; // gap between nodes

      levels.forEach((depth, newLevelIndex) => {
        const levelNodes = byLevel.get(depth)!;
        // Sort by original Y position to maintain relative order
        levelNodes.sort((a, b) => a.position.y - b.position.y);

        let currentY = 0;
        levelNodes.forEach((node) => {
          const targetNode = baseFiltered.find(n => n.id === node.id);
          if (targetNode) {
            targetNode.position = {
              x: newLevelIndex * COMPACT_H_SPACING,
              y: currentY,
            };
            const nodeHeight = estimateNodeHeightLocal(node.data.label || '');
            currentY += nodeHeight + V_GAP;
          }
        });
      });

      return baseFiltered;
    }

    // 1. Identify which nodes match the explicit filters (Search Term OR Selected Domain)
    const directMatches = new Set<string>();
    
    nodes.forEach(node => {
        const matchesSearch = !searchTerm.trim() || (node.data.label ?? '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesDomain = !selectedDomain || node.data.domain === selectedDomain;
        
        if (matchesSearch && matchesDomain) {
            directMatches.add(node.id);
        }
    });

    // 2. Identify neighbors of direct matches (if domain filtering is active)
    // We want to show the context: Parents and Children of the selected domain/search hits.
    const contextNodes = new Set<string>();
    
    if (selectedDomain || searchTerm.trim()) {
        allLinks.forEach(link => {
            const p = link.parentSkillID;
            const c = link.offspringSkillID;
            
            // If parent is a match, include child
            if (directMatches.has(p)) contextNodes.add(c);
            
            // If child is a match, include parent
            if (directMatches.has(c)) contextNodes.add(p);
        });
    }

    // 3. Combine matches + context
    const visibleIds = new Set(Array.from(directMatches).concat(Array.from(contextNodes)));

    const baseFiltered = nodes.map(node => {
      // If we have filters active (domain or search), use the set logic.
      // If NO filters active, show everything (unless hidden by default? No, usually show all)
      const isVisible = (selectedDomain || searchTerm.trim()) ? visibleIds.has(node.id) : true;
      
      return {
        ...node,
        hidden: !isVisible,
      };
    });

    // Helper: estimate node height based on label length
    const estimateNodeHeight = (label: string): number => {
      const NODE_MIN_WIDTH = 120; // from node style
      const CHAR_WIDTH = 7; // approximate pixels per character
      const LINE_HEIGHT = 20; // approximate line height
      const PADDING = 24; // vertical padding
      
      const charsPerLine = Math.floor(NODE_MIN_WIDTH / CHAR_WIDTH);
      const numLines = Math.ceil(label.length / charsPerLine);
      return Math.max(50, numLines * LINE_HEIGHT + PADDING);
    };

    // If filtering is active, recalculate depths using only visible parents
    if (selectedDomain || searchTerm.trim()) {
      const visibleNodes = baseFiltered.filter(n => !n.hidden);
      const visibleIds = new Set(visibleNodes.map(n => n.id));
      
      // Build parent map using only visible parents from allLinks
      const visibleParents = new Map<string, string[]>();
      allLinks.forEach(link => {
        const parentId = link.parentSkillID;
        const childId = link.offspringSkillID;
        if (visibleIds.has(parentId) && visibleIds.has(childId)) {
          if (!visibleParents.has(childId)) visibleParents.set(childId, []);
          visibleParents.get(childId)!.push(parentId);
        }
      });
      
      // Recalculate depths for visible nodes using only visible parents
      const depths = new Map<string, number>();
      
      const getDepth = (nodeId: string, visited: Set<string> = new Set()): number => {
        if (depths.has(nodeId)) return depths.get(nodeId)!;
        if (visited.has(nodeId)) return 0; // Cycle detection
        visited.add(nodeId);
        
        const parentIds = visibleParents.get(nodeId) || [];
        if (parentIds.length === 0) {
          depths.set(nodeId, 0);
          return 0;
        }
        
        const maxParentDepth = Math.max(...parentIds.map(pid => getDepth(pid, new Set(visited))));
        const depth = maxParentDepth + 1;
        depths.set(nodeId, depth);
        return depth;
      };
      
      visibleNodes.forEach(n => getDepth(n.id));
      
      // Group by recalculated depth
      const byLevel = new Map<number, typeof visibleNodes>();
      visibleNodes.forEach(node => {
        const level = depths.get(node.id) || 0;
        if (!byLevel.has(level)) byLevel.set(level, []);
        byLevel.get(level)!.push(node);
      });
      
      // Get unique levels and compact them
      const levels = Array.from(byLevel.keys()).sort((a, b) => a - b);
      const COMPACT_H_SPACING = 300;
      const V_GAP = 15; // gap between nodes
      
      // Reposition nodes within each compacted level with dynamic spacing
      levels.forEach((depth, newLevelIndex) => {
        const levelNodes = byLevel.get(depth)!;
        // Sort by original Y position to maintain relative order
        levelNodes.sort((a, b) => a.position.y - b.position.y);
        
        let currentY = 0;
        levelNodes.forEach((node) => {
          const targetNode = baseFiltered.find(n => n.id === node.id);
          if (targetNode) {
            targetNode.position = {
              x: newLevelIndex * COMPACT_H_SPACING,
              y: currentY,
            };
            const nodeHeight = estimateNodeHeight(node.data.label || '');
            currentY += nodeHeight + V_GAP;
          }
        });
      });
    }

    return baseFiltered;
  }, [nodes, searchTerm, selectedDomain, allLinks, filteredSkillIds, allSkills]);

  // Filter edges to only show connections between visible nodes
  const filteredEdges = useMemo(() => {
    const visibleNodeIds = new Set(filteredNodes.filter(n => !n.hidden).map(n => n.id));
    return edges.map(edge => ({
      ...edge,
      hidden: !(visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target)),
    }));
  }, [edges, filteredNodes]);

  // Export selected/filtered nodes as JSON
  const exportSelection = useCallback(() => {
    // Start with visible (filtered) nodes
    const visibleNodeIds = new Set(filteredNodes.filter(n => !n.hidden).map(n => n.id));
    
    // If there are selected nodes, use only those that are also visible
    const idsToExport = selectedNodes.size > 0 
      ? new Set(Array.from(selectedNodes).filter(id => visibleNodeIds.has(id)))
      : visibleNodeIds;
    
    const exportedSkills = allSkills.filter(s => idsToExport.has(s.id));
    const exportedLinks = allLinks.filter(l => 
      idsToExport.has(l.parentSkillID) && 
      idsToExport.has(l.offspringSkillID)
    );
    
    // Build skill name lookup
    const skillNameMap = new Map(exportedSkills.map(s => [s.id, s.skillName]));
    
    // Convert to SemanticLink format
    const semanticLinks = exportedLinks.map(link => {
      const parentName = skillNameMap.get(link.parentSkillID) || 'Unknown';
      const offspringName = skillNameMap.get(link.offspringSkillID) || 'Unknown';
      return {
        statement: `${parentName} enables ${offspringName}`,
        source_term: parentName,
        target_term: offspringName,
        forward_relation: 'enables',
        inverse_relation: 'requires',
        domain: 'education',
        subject: 'skills',
        metadata: {
          parent_skill_id: link.parentSkillID,
          offspring_skill_id: link.offspringSkillID,
        },
      };
    });
    
    const exportData = {
      $schema: 'https://example.org/schemas/semantic_link.schema.json',
      exportedAt: new Date().toISOString(),
      count: semanticLinks.length,
      skills: exportedSkills,
      links: semanticLinks,
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `skills-export-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [selectedNodes, filteredNodes, allSkills, allLinks]);

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedNodes(new Set());
  }, []);

  // Handle pane click (clicking on background) - clear edge highlighting
  const onPaneClick = useCallback(() => {
    setClickedNodeId(null);
    setEdges(eds => eds.map(edge => ({
      ...edge,
      style: DEFAULT_EDGE_STYLE,
      animated: false,
      markerEnd: { type: MarkerType.ArrowClosed, color: '#666' },
      zIndex: 0,
    })));
  }, [setEdges]);

  // Update nodes to highlight connected ones when a node is clicked
  const displayNodes = useMemo(() => {
    if (!clickedNodeId) {
      return filteredNodes;
    }
    
    // Find all nodes connected to the clicked node
    const connectedIds = new Set<string>();
    connectedIds.add(clickedNodeId);
    
    filteredEdges.forEach(edge => {
      if (edge.source === clickedNodeId) connectedIds.add(edge.target);
      if (edge.target === clickedNodeId) connectedIds.add(edge.source);
    });
    
    return filteredNodes.map(node => ({
      ...node,
      style: {
        ...node.style,
        opacity: connectedIds.has(node.id) ? 1 : 0.3,
        boxShadow: node.id === clickedNodeId ? '0 0 20px 5px rgba(255, 87, 34, 0.7)' : undefined,
        border: node.id === clickedNodeId ? '3px solid #FF5722' : (node.style?.border || '2px solid #333'),
      },
    }));
  }, [filteredNodes, clickedNodeId, filteredEdges]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress size={60} />
        <Typography sx={{ ml: 2 }}>Loading skills graph...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <ReactFlowProvider>
      <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      <ReactFlow
        nodes={displayNodes}
        edges={filteredEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        onSelectionChange={onSelectionChange}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.1}
        maxZoom={2}
        selectionOnDrag
        panOnDrag
        selectNodesOnDrag
      >
        <FitViewOnFilterChange selectedDomain={selectedDomain} searchTerm={searchTerm} />
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} />
        <Controls />
        <MiniMap 
          nodeColor={(node) => {
            const domain = node.data?.domain as string;
            return DOMAIN_COLORS[domain] || DOMAIN_COLORS.default;
          }}
          maskColor="rgba(0,0,0,0.2)"
        />
        
        {/* Top Panel: Search & Actions */}
        {showSearch && (
          <Panel position="top-left">
            <Paper sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Search skills..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: searchTerm && (
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
                sx={{ width: 250 }}
              />
              <Divider orientation="vertical" flexItem />
              <Chip 
                label={`${nodes.length} skills`} 
                size="small" 
                color="primary" 
              />
              <Chip 
                label={`${edges.length} links`} 
                size="small" 
                color="secondary" 
              />
              {selectedNodes.size > 0 && (
                <>
                  <Chip 
                    label={`${selectedNodes.size} selected`} 
                    size="small" 
                    color="success"
                    onDelete={clearSelection}
                  />
                </>
              )}
            </Paper>
          </Panel>
        )}
        
        {/* Export Button */}
        <Panel position="top-right">
          <Paper sx={{ p: 1 }}>
            <Tooltip title={
              selectedNodes.size > 0 
                ? 'Export selected skills as JSON' 
                : (selectedDomain || searchTerm.trim()) 
                  ? 'Export filtered skills as JSON'
                  : 'Export all skills as JSON'
            }>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={exportSelection}
                color="primary"
              >
                Export {selectedNodes.size > 0 
                  ? `(${selectedNodes.size})` 
                  : (selectedDomain || searchTerm.trim()) 
                    ? 'Filtered' 
                    : 'All'}
              </Button>
            </Tooltip>
          </Paper>
        </Panel>

        {/* Legend */}
        {showDomains && (
          <Panel position="bottom-left">
            <Paper sx={{ p: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 1, display: 'block' }}>
                Domains {selectedDomain && <span style={{ fontWeight: 'normal' }}>– click again to show all</span>}
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {Object.entries(DOMAIN_COLORS).filter(([k]) => k !== 'default').map(([domain, color]) => (
                  <Chip
                    key={domain}
                    label={domain}
                    size="small"
                    clickable
                    onClick={() => setSelectedDomain(selectedDomain === domain ? null : domain)}
                    sx={{ 
                      bgcolor: color, 
                      color: domain === 'CREATIVITY' ? '#333' : '#fff',
                      fontSize: '10px',
                      height: '20px',
                      opacity: selectedDomain && selectedDomain !== domain ? 0.4 : 1,
                      border: selectedDomain === domain ? '2px solid #000' : 'none',
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 1,
                        transform: 'scale(1.05)',
                      },
                    }}
                  />
                ))}
              </Stack>
              <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary', fontStyle: 'italic' }}>
                💡 Click a domain to filter • Click a skill to see connections
              </Typography>
            </Paper>
          </Panel>
        )}

      </ReactFlow>

      {/* Skill Details Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Box sx={{ width: 360, p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Skill Details</Typography>
            <IconButton onClick={() => setDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          
          {selectedSkill && (
            <>
              <Typography variant="h5" sx={{ mb: 1 }}>
                {selectedSkill.skillName}
              </Typography>
              
              <Chip 
                label={guessDomain(selectedSkill.skillName)} 
                size="small" 
                sx={{ 
                  mb: 2,
                  bgcolor: DOMAIN_COLORS[guessDomain(selectedSkill.skillName)],
                  color: guessDomain(selectedSkill.skillName) === 'CREATIVITY' ? '#333' : '#fff',
                }}
              />
              
              {selectedSkill.skillDescription && (
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  {selectedSkill.skillDescription}
                </Typography>
              )}
              
              {selectedSkill.developmentAge && (
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>Development Age:</strong> {selectedSkill.developmentAge}
                </Typography>
              )}
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Criteria
              </Typography>
              <List dense>
                {[selectedSkill.criteria1, selectedSkill.criteria2, selectedSkill.criteria3, selectedSkill.criteria4, selectedSkill.criteria5]
                  .filter(Boolean)
                  .map((c, i) => (
                    <ListItem key={i}>
                      <ListItemText 
                        primary={`${i + 1}. ${c}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
              </List>
              
              <Divider sx={{ my: 2 }} />
              
              <Button
                fullWidth
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => {
                  const links = allLinks.filter(l => 
                    l.parentSkillID === selectedSkill.id || 
                    l.offspringSkillID === selectedSkill.id
                  );
                  const relatedIds = new Set([
                    selectedSkill.id,
                    ...links.map((l: SkillLink) => l.parentSkillID),
                    ...links.map((l: SkillLink) => l.offspringSkillID),
                  ]);
                  const relatedSkills = allSkills.filter(s => relatedIds.has(s.id));
                  
                  const exportData = {
                    focusSkill: selectedSkill,
                    relatedSkills,
                    links,
                    exportedAt: new Date().toISOString(),
                  };
                  
                  const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `skill-${selectedSkill.id}-graph.json`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
              >
                Export Skill + Connections
              </Button>
            </>
          )}
        </Box>
      </Drawer>
      </Box>
    </ReactFlowProvider>
  );
};

export default GraphExplorer;
